/**
 * DEV-ONLY: Global design overlay. Activates via ?overlay=image.png.
 * Persists state via DevtoolsSession (route-scoped localStorage).
 * Overlay image is portaled into #design-overlay-root so it scrolls with page content.
 * Panel is registered with DevtoolsStack so it stacks with other devtools.
 */
import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import DesignOverlayPanel from "./DesignOverlayPanel";
import DevtoolsPanelShell from "./DevtoolsPanelShell";
import { useDevtoolsStack } from "./DevtoolsStackContext";
import { useDevtoolsSession } from "./DevtoolsSessionContext";
import "./DesignOverlay.css";

const OVERLAY_ROOT_ID = "design-overlay-root";

const DEFAULTS = { opacity: 0.5, offsetX: 0, offsetY: 0, mode: "normal" };

function roundPx(n) {
  return Math.round(Number(n)) || 0;
}

export default function DesignOverlayGlobal() {
  const [searchParams] = useSearchParams();
  const stack = useDevtoolsStack();
  const sessionApi = useDevtoolsSession();
  const sessionOverlay = sessionApi?.session?.overlay;

  // Query param compatibility: if overlay param exists it should win.
  const overlayFileFromUrl = searchParams.get("overlay");
  const overlayFile = overlayFileFromUrl || sessionOverlay?.file || "";
  const overlayEnabled = Boolean(sessionOverlay?.enabled && overlayFile);

  const [naturalWidth, setNaturalWidth] = useState(null);
  const [naturalHeight, setNaturalHeight] = useState(null);
  const visible = sessionOverlay?.visible ?? true;
  const opacity = sessionOverlay?.opacity ?? DEFAULTS.opacity;
  const offsetX = sessionOverlay?.offsetX ?? DEFAULTS.offsetX;
  const offsetY = sessionOverlay?.offsetY ?? DEFAULTS.offsetY;
  const mode = sessionOverlay?.mode ?? DEFAULTS.mode;
  const widthPx = sessionOverlay?.widthPx ?? null;
  const heightPx = sessionOverlay?.heightPx ?? null;
  const lockAspectRatio = sessionOverlay?.lockAspectRatio ?? true;

  useEffect(() => {
    if (!overlayEnabled) return;
    setNaturalWidth(null);
    setNaturalHeight(null);
  }, [overlayEnabled, overlayFile]);

  const handleImageLoad = useCallback(
    (e) => {
      const img = e.target;
      const nw = img.naturalWidth;
      const nh = img.naturalHeight;
      if (!nw || !nh) return;
      setNaturalWidth(nw);
      setNaturalHeight(nh);
      // If no size persisted yet, initialize to natural size once known.
      if (!sessionApi) return;
      sessionApi.setSession((prev) => {
        const cur = prev.overlay || {};
        if (cur.widthPx != null || cur.heightPx != null) return prev;
        return {
          overlay: {
            ...cur,
            enabled: true,
            file: overlayFile,
            visible: cur.visible ?? true,
            widthPx: nw,
            heightPx: nh,
          },
        };
      });
    },
    [sessionApi, overlayFile]
  );

  const aspectRatio = naturalWidth != null && naturalHeight != null ? naturalWidth / naturalHeight : 1;

  const handleWidthChange = useCallback(
    (val) => {
      const w = roundPx(val);
      if (!sessionApi) return;
      sessionApi.setSession((prev) => {
        const cur = prev.overlay || {};
        const next = { ...cur, enabled: true, file: overlayFile, widthPx: w };
        if (lockAspectRatio && naturalWidth != null && naturalHeight != null) {
          next.heightPx = roundPx(w / aspectRatio);
        }
        return { overlay: next };
      });
    },
    [sessionApi, overlayFile, lockAspectRatio, naturalWidth, naturalHeight, aspectRatio]
  );

  const handleHeightChange = useCallback(
    (val) => {
      const h = roundPx(val);
      if (!sessionApi) return;
      sessionApi.setSession((prev) => {
        const cur = prev.overlay || {};
        const next = { ...cur, enabled: true, file: overlayFile, heightPx: h };
        if (lockAspectRatio && naturalWidth != null && naturalHeight != null) {
          next.widthPx = roundPx(h * aspectRatio);
        }
        return { overlay: next };
      });
    },
    [sessionApi, overlayFile, lockAspectRatio, naturalWidth, naturalHeight, aspectRatio]
  );

  const handleReset = useCallback(() => {
    if (!sessionApi) return;
    sessionApi.setSession((prev) => {
      const cur = prev.overlay || {};
      return {
        overlay: {
          ...cur,
          enabled: Boolean(overlayFile),
          file: overlayFile,
          visible: true,
          opacity: DEFAULTS.opacity,
          offsetX: DEFAULTS.offsetX,
          offsetY: DEFAULTS.offsetY,
          mode: DEFAULTS.mode,
          lockAspectRatio: true,
          widthPx: naturalWidth != null ? naturalWidth : cur.widthPx ?? null,
          heightPx: naturalHeight != null ? naturalHeight : cur.heightPx ?? null,
        },
      };
    });
  }, [sessionApi, overlayFile, naturalWidth, naturalHeight]);

  useEffect(() => {
    if (!stack) return;
    if (!overlayEnabled) {
      stack.unregisterPanel("overlay");
      return;
    }
    stack.registerPanel("overlay", () => (
      <DevtoolsPanelShell title="Design Overlay">
        <DesignOverlayPanel
          visible={visible}
          onVisibleChange={(v) =>
            sessionApi?.setSession((prev) => ({
              overlay: { ...(prev.overlay || {}), enabled: true, file: overlayFile, visible: v },
            }))
          }
          opacity={opacity}
          onOpacityChange={(v) =>
            sessionApi?.setSession((prev) => ({
              overlay: { ...(prev.overlay || {}), enabled: true, file: overlayFile, opacity: v },
            }))
          }
          offsetX={offsetX}
          onOffsetXChange={(v) =>
            sessionApi?.setSession((prev) => ({
              overlay: { ...(prev.overlay || {}), enabled: true, file: overlayFile, offsetX: v },
            }))
          }
          offsetY={offsetY}
          onOffsetYChange={(v) =>
            sessionApi?.setSession((prev) => ({
              overlay: { ...(prev.overlay || {}), enabled: true, file: overlayFile, offsetY: v },
            }))
          }
          mode={mode}
          onModeChange={(v) =>
            sessionApi?.setSession((prev) => ({
              overlay: { ...(prev.overlay || {}), enabled: true, file: overlayFile, mode: v },
            }))
          }
          onReset={handleReset}
          widthPx={widthPx}
          heightPx={heightPx}
          onWidthChange={handleWidthChange}
          onHeightChange={handleHeightChange}
          lockAspectRatio={lockAspectRatio}
          onLockAspectRatioChange={(v) =>
            sessionApi?.setSession((prev) => ({
              overlay: { ...(prev.overlay || {}), enabled: true, file: overlayFile, lockAspectRatio: v },
            }))
          }
          naturalWidth={naturalWidth}
          naturalHeight={naturalHeight}
        />
      </DevtoolsPanelShell>
    ));
    return () => stack.unregisterPanel("overlay");
  }, [
    overlayEnabled,
    overlayFile,
    stack,
    sessionApi,
    visible,
    opacity,
    offsetX,
    offsetY,
    mode,
    widthPx,
    heightPx,
    lockAspectRatio,
    naturalWidth,
    naturalHeight,
    handleWidthChange,
    handleHeightChange,
    handleReset,
  ]);

  if (!overlayEnabled) return null;

  const imageSrc = `/static/img/${overlayFile}`;
  const overlayRoot = typeof document !== "undefined" ? document.getElementById(OVERLAY_ROOT_ID) : null;

  const overlayContent = visible ? (
    <div className="design-overlay" aria-hidden>
      <img
        src={imageSrc}
        alt="Design comp overlay"
        className={`design-overlay__image ${mode === "difference" ? "design-overlay__image--difference" : ""}`}
        style={{
          opacity,
          transform: `translate(${offsetX}px, ${offsetY}px)`,
          ...(widthPx != null && { width: `${widthPx}px` }),
          ...(heightPx != null && { height: `${heightPx}px` }),
          ...(widthPx != null && heightPx != null && { objectFit: "fill" }),
        }}
        onLoad={handleImageLoad}
      />
    </div>
  ) : null;

  return <>{overlayRoot && createPortal(overlayContent, overlayRoot)}</>;
}
